import React, { useState } from 'react';
import { Heart, Users, Sparkles, MapPin, Clock, CheckCircle, ArrowRight, Briefcase, Star, X, Megaphone, Camera, HeartHandshake } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const positions = [
  {
    title: 'Organizzatore Eventi',
    icon: Sparkles,
    type: 'Part-time / Full-time',
    location: 'Varie citta\'',
    description: 'Gestisci e coordina i nostri eventi di speed dating sul territorio. Dalla logistica alla gestione dei partecipanti, sarai il punto di riferimento per serate indimenticabili.',
    requirements: [
      'Ottime capacita\' organizzative e comunicative',
      'Disponibilita\' serale e nei weekend',
      'Esperienza nell\'organizzazione eventi (preferibile)',
      'Attitudine al problem-solving',
    ],
  },
  {
    title: 'PR / Promoter',
    icon: Megaphone,
    type: 'Part-time',
    location: 'Varie citta\'',
    description: 'Promuovi i nostri eventi e attrai nuovi partecipanti. Costruisci relazioni con locali, attivita\' commerciali e community locali per far crescere FlashCrush nella tua zona.',
    requirements: [
      'Spiccate doti relazionali e comunicative',
      'Conoscenza del territorio e della vita sociale locale',
      'Esperienza in promozione o vendita (preferibile)',
      'Forte presenza sui social media',
    ],
  },
  {
    title: 'Dating Coach',
    icon: HeartHandshake,
    type: 'Freelance',
    location: 'Remoto / In presenza',
    description: 'Accompagna i nostri clienti in un percorso di crescita personale nelle relazioni. Offri sessioni di coaching individuali e di gruppo per aiutarli a trovare l\'amore.',
    requirements: [
      'Formazione in coaching o psicologia relazionale',
      'Esperienza nel settore del dating o relazioni',
      'Empatia e capacita\' di ascolto',
      'Certificazioni nel coaching (preferibile)',
    ],
  },
  {
    title: 'Social Media Manager',
    icon: Camera,
    type: 'Part-time / Remoto',
    location: 'Remoto',
    description: 'Cura la nostra presenza online, crea contenuti coinvolgenti e gestisci le campagne social per raggiungere nuovi single e far crescere la community FlashCrush.',
    requirements: [
      'Esperienza nella gestione di social media aziendali',
      'Creativita\' e capacita\' di storytelling visivo',
      'Conoscenza di Meta Business Suite, Canva, strumenti di analytics',
      'Sensibilita\' per il settore lifestyle e relazioni',
    ],
  },
];

const LavoraConNoi = () => {
  const navigate = useNavigate();
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    position: '',
    experience: '',
    motivation: '',
    availability: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openApplicationForm = (positionTitle: string) => {
    setSelectedPosition(positionTitle);
    setFormData(prev => ({ ...prev, position: positionTitle }));
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/db-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          action: 'insert',
          table: 'job_applications',
          data: formData,
        }),
      });

      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error?.message || json.error || 'Errore durante l\'invio della candidatura');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Si e\' verificato un errore. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const values = [
    {
      icon: Heart,
      title: 'Passione',
      description: 'Crediamo nel potere delle connessioni autentiche e mettiamo il cuore in tutto cio\' che facciamo.',
    },
    {
      icon: Users,
      title: 'Squadra',
      description: 'Lavoriamo come una famiglia, supportandoci a vicenda per raggiungere obiettivi condivisi.',
    },
    {
      icon: Star,
      title: 'Eccellenza',
      description: 'Puntiamo sempre al meglio, curando ogni dettaglio per offrire esperienze straordinarie.',
    },
    {
      icon: Sparkles,
      title: 'Innovazione',
      description: 'Sperimentiamo continuamente nuovi format e approcci per restare al passo con i tempi.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFF5F7]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#D91C5C] to-[#8B1538] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Heart className="h-6 w-6 text-white fill-white" />
              <span className="text-xl font-bold text-white">FlashCrush</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-white/80 hover:text-white transition-colors text-sm font-medium"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 px-4 bg-gradient-to-b from-[#FFF5F7] to-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#D91C5C]/10 text-[#D91C5C] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Briefcase className="h-4 w-4" />
            Opportunita\' di Lavoro
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Unisciti al Team<br />
            <span className="text-[#D91C5C]">FlashCrush</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
            Cerchiamo persone appassionate, creative e piene di energia che vogliano fare la differenza
            nel mondo degli eventi per single. Scopri le nostre posizioni aperte e candidati!
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">I Nostri Valori</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Quello che ci unisce e ci guida ogni giorno.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 bg-[#D91C5C]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <v.icon className="h-8 w-8 text-[#D91C5C]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Posizioni Aperte</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Trova il ruolo perfetto per te e inizia la tua avventura con FlashCrush.
            </p>
          </div>
          <div className="space-y-6">
            {positions.map((pos, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:border-[#D91C5C]/20 transition-all">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-start gap-5">
                    <div className="w-14 h-14 bg-[#D91C5C]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <pos.icon className="h-7 w-7 text-[#D91C5C]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{pos.title}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {pos.type}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {pos.location}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4 leading-relaxed">{pos.description}</p>
                      <div className="mb-5">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Requisiti:</h4>
                        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                          {pos.requirements.map((req, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle className="h-4 w-4 text-[#D91C5C] mt-0.5 flex-shrink-0" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <button
                        onClick={() => openApplicationForm(pos.title)}
                        className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-6 py-2.5 rounded-xl font-semibold transition-all inline-flex items-center gap-2 text-sm"
                      >
                        Candidati Ora
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="application-form" className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Invia la Tua Candidatura
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Compila il modulo e raccontaci chi sei. Ti risponderemo il prima possibile!
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#FFF5F7] rounded-2xl p-12 border border-gray-100 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Candidatura Inviata!</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Grazie per il tuo interesse! Abbiamo ricevuto la tua candidatura e ti contatteremo al piu\' presto.
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-6 py-3 rounded-xl font-semibold transition-all"
              >
                Torna alla Home
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#FFF5F7] rounded-2xl p-8 md:p-10 border border-gray-100">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <X className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome *</label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      value={formData.full_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors bg-white"
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors bg-white"
                      placeholder="mario@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors bg-white"
                      placeholder="+39 333 1234567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Citta' *</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors bg-white"
                      placeholder="Roma"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Posizione *</label>
                  <select
                    name="position"
                    required
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors bg-white"
                  >
                    <option value="">Seleziona posizione</option>
                    {positions.map(p => (
                      <option key={p.title} value={p.title}>{p.title}</option>
                    ))}
                    <option value="Altro">Altro / Candidatura spontanea</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Esperienza Rilevante</label>
                  <textarea
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors resize-none bg-white"
                    placeholder="Descrivi le tue esperienze lavorative rilevanti per questa posizione..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Perche' Vuoi Lavorare con Noi</label>
                  <textarea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors resize-none bg-white"
                    placeholder="Raccontaci cosa ti motiva e perche' vorresti far parte del team FlashCrush..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Disponibilita'</label>
                  <input
                    type="text"
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors bg-white"
                    placeholder="Es: Da subito, sere e weekend, part-time..."
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#D91C5C] hover:bg-[#C01850] text-white py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        Invia Candidatura
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Inviando questo modulo acconsenti al trattamento dei tuoi dati personali secondo la nostra Privacy Policy.
                  </p>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#A81444] text-white py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="h-5 w-5 fill-white" />
            <span className="text-lg font-bold">FlashCrush</span>
          </div>
          <p className="text-white/80 text-sm">&copy; 2026 FlashCrush. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
};

export default LavoraConNoi;
